import React, { useState, useMemo, useCallback } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { allData } from '../../1-bibliotheque-prix/data/ServicesData';
import { DevisLine, BenchmarkStats, DevisSummary } from '../types';
import { computeBenchmarkStats } from '../utils/benchmarkStats';
import { computeEcart } from '../utils/benchmarkStats';
import DevisLineRow from './DevisLineRow';
import DevisSummaryBar from './DevisSummaryBar';

let nextId = 100;
const createEmptyLine = (): DevisLine => ({
  id: String(nextId++),
  prestation: '',
  quantite: 0,
  unite: 'm²',
  prixUnitaire: 0,
  matchedOuvrageId: null,
  matchConfidence: 0,
  isManualMatch: false,
});

const MOCK_LINES: DevisLine[] = [
  { id: '1', prestation: 'Cloison BA13 standard', quantite: 220, unite: 'm²', prixUnitaire: 32.00, matchedOuvrageId: 1, matchConfidence: 0.85, isManualMatch: false },
  { id: '2', prestation: 'Béton armé fondations', quantite: 180, unite: 'm³', prixUnitaire: 91.50, matchedOuvrageId: 2, matchConfidence: 0.80, isManualMatch: false },
  { id: '3', prestation: 'Carrelage sol 20x20', quantite: 350, unite: 'm²', prixUnitaire: 46.00, matchedOuvrageId: 3, matchConfidence: 0.90, isManualMatch: false },
  { id: '4', prestation: 'Électricité générale', quantite: 600, unite: 'm²', prixUnitaire: 135.00, matchedOuvrageId: 4, matchConfidence: 0.75, isManualMatch: false },
  { id: '5', prestation: 'Plomberie sanitaire complète', quantite: 30, unite: 'forfait', prixUnitaire: 2800.00, matchedOuvrageId: 5, matchConfidence: 0.88, isManualMatch: false },
  { id: '6', prestation: 'Peinture murs et plafonds', quantite: 1200, unite: 'm²', prixUnitaire: 14.50, matchedOuvrageId: 10, matchConfidence: 0.82, isManualMatch: false },
  { id: '7', prestation: 'Isolation combles perdus', quantite: 400, unite: 'm²', prixUnitaire: 19.00, matchedOuvrageId: 8, matchConfidence: 0.78, isManualMatch: false },
  { id: '8', prestation: 'Menuiseries PVC double vitrage', quantite: 95, unite: 'm²', prixUnitaire: 380.00, matchedOuvrageId: 9, matchConfidence: 0.83, isManualMatch: false },
  { id: '9', prestation: 'Doublage thermique sur rails', quantite: 160, unite: 'm²', prixUnitaire: 34.50, matchedOuvrageId: null, matchConfidence: 0, isManualMatch: false },
];

const DevisTable: React.FC = () => {
  const [lines, setLines] = useState<DevisLine[]>(MOCK_LINES);
  const [nomDevis, setNomDevis] = useState('Devis TCE — Résidence Les Music');
  const [fournisseur, setFournisseur] = useState('BTP Solutions Sud-Est');

  const benchmarkMap = useMemo(() => {
    const map = new Map<number, BenchmarkStats>();
    for (const ouvrage of allData) {
      map.set(ouvrage.id, computeBenchmarkStats(ouvrage));
    }
    return map;
  }, []);

  const summary = useMemo((): DevisSummary => {
    let totalDevis = 0;
    let totalBenchmarkAvg = 0;
    let linesMatched = 0;

    for (const line of lines) {
      const lineTotal = line.quantite * line.prixUnitaire;
      totalDevis += lineTotal;

      if (line.matchedOuvrageId !== null) {
        linesMatched++;
        const stats = benchmarkMap.get(line.matchedOuvrageId);
        if (stats && stats.count > 0) {
          totalBenchmarkAvg += line.quantite * stats.avg;
        }
      }
    }

    const ecartTotal = totalDevis - totalBenchmarkAvg;
    const ecartPercent =
      totalBenchmarkAvg > 0
        ? computeEcart(totalDevis, totalBenchmarkAvg)
        : 0;

    return {
      totalDevis,
      totalBenchmarkAvg,
      ecartTotal,
      ecartPercent,
      linesMatched,
      linesTotal: lines.length,
    };
  }, [lines, benchmarkMap]);

  const handleUpdateLine = useCallback(
    (id: string, updated: DevisLine) => {
      setLines((prev) => prev.map((l) => (l.id === id ? updated : l)));
    },
    []
  );

  const handleDeleteLine = useCallback((id: string) => {
    setLines((prev) => {
      const filtered = prev.filter((l) => l.id !== id);
      return filtered.length === 0 ? [createEmptyLine()] : filtered;
    });
  }, []);

  const handleAddLine = () => {
    setLines((prev) => [...prev, createEmptyLine()]);
  };

  return (
    <div>
      {/* Metadata */}
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Nom du devis
          </label>
          <input
            type="text"
            value={nomDevis}
            onChange={(e) => setNomDevis(e.target.value)}
            placeholder="Ex: Devis lot plâtrerie - Résidence Bleu"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Fournisseur
          </label>
          <input
            type="text"
            value={fournisseur}
            onChange={(e) => setFournisseur(e.target.value)}
            placeholder="Ex: Entreprise Martin"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Summary */}
      <DevisSummaryBar summary={summary} />

    <section className="p-6 bg-white shadow rounded-xl">

      {/* Table */}
      <div className="overflow-visible" style={{ minHeight: '28rem' }}>
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Prestation
              </th>
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right w-20">
                Qté
              </th>
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center w-20">
                Unité
              </th>
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right w-24">
                PU HT
              </th>
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right w-28">
                Total HT
              </th>
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Correspondance
              </th>
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Benchmark
              </th>
              <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">
                Écart
              </th>
              <th className="px-3 py-3 w-10" />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <DevisLineRow
                key={line.id}
                line={line}
                ouvrages={allData}
                benchmarkStats={
                  line.matchedOuvrageId
                    ? benchmarkMap.get(line.matchedOuvrageId) ?? null
                    : null
                }
                onUpdate={(updated) => handleUpdateLine(line.id, updated)}
                onDelete={() => handleDeleteLine(line.id)}
              />
            ))}
            {/* Ligne bouton ajouter */}
            <tr>
              <td colSpan={9} className="py-2">
                <button
                  onClick={handleAddLine}
                  className="mx-auto flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-violet-600 hover:bg-violet-50 rounded-md transition-colors"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Ajouter une ligne
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    </div>
  );
};

export default DevisTable;
